import { useEffect, useState, useContext } from "react";
import { useNavigate } from "react-router-dom";
import partCodesService from "services/part-code-service";
import { AuthContext } from "context";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import CircularProgress from "@mui/material/CircularProgress";

// E4A React components
import DashboardLayout from "e4acomponents/LayoutContainers/DashboardLayout";
import DashboardNavbar from "e4acomponents/Navbars/DashboardNavbar";
import Footer from "e4acomponents/Footer";

// Custom components
import GeneralInfoFields from "./general-info-fields";
import ImageField from "./image-field";
import AttachmentsField from "./attachments-field";
import AttributesFields from "./attributes-fields";

const NewPart = () => {
  const navigate = useNavigate();
  const [part, setPart] = useState({
    description: "N/A",
    unique_num: "",
    revision_num: 1,
    generatedUID: "",
    manufacturer: "",
    manufacturer_part_number: "",
    preferred_mpn: "0",
    vendors: "",
    preferred_vendor: "1",
    vendor_part_number: "",
    mpn: [],
    preferred_mpn: 0,
    partCategory: {
      parentCategory: "",
      categoryCode: "",
      id: "",
      attributes: "",
      has_attributes: "",
    },
    attributes: ""
  });
  
  // Raises error flags to unfilled formfields
  const [error, setError] = useState({})
  const [showAttributes, setShowAttributes] = useState(false);
  const [image, setImage] = useState("");
  const [files, setFiles] = useState({});
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [units, setUnits] = useState({});
  const [usedPartNumbers, setUsedPartNumbers] = useState("");

  const authContext = useContext(AuthContext);

  useEffect(() => {
    (async () => {
      setIsVisible(true);
      setIsLoading(false);
      const usedPartNumbers = await partCodesService.getUsedPartNumbers();
      setUsedPartNumbers(usedPartNumbers.data.attributes);
    })();
  }, []);

  /*
  There is two attributes field in the variable part,
  The one under partCategory is used to generate the fields and options
  The one directly under part is used to log the entered values for creating the part code
  */

  //Handles the generation/reset of attribute fields
  useEffect(() => {
    // Removes attributes field from part if the selected part category has none.
    if (part.partCategory.has_attributes === false) {
      setPart(prev => ({
        ...prev,
        attributes: ""
      }));
      setShowAttributes(false);
      // Generates the attributes fields if the selected part category has some.
    } else {
      setPart(prev => ({
        ...prev,
        attributes: Object.fromEntries(
          Object.values(part.partCategory.attributes).map(attr => [attr.attribute, ""])
        )
      }));
      setShowAttributes(true);
    }
  }, [part.partCategory.attributes])

  // Generates the description of the partcode based on the entered attributes
  useEffect(() => {
    if (part.attributes && typeof part.attributes === 'object' && Object.keys(part.attributes).length > 0) {
      let description = part.partCategory.categoryCode + " ";

      let categoryAttributes = part.partCategory.attributes ? part.partCategory.attributes : {};
      if (categoryAttributes[0]) {
        let keysToDisplay = [];
        if (categoryAttributes[0].enableCondAttRules) {
          const type = part.attributes[categoryAttributes[0].attribute];
          keysToDisplay = categoryAttributes[0][type].split(";");
        } else {
          keysToDisplay = Object.keys(categoryAttributes);
        }

        for (const key in categoryAttributes) {
          if (!keysToDisplay.includes(key)) {
            delete categoryAttributes[key];
          }
        }

      }

      for (const key in categoryAttributes) {
        const label = categoryAttributes[key].attribute;
        const value = part.attributes ? part.attributes[label] : '';

        if (units[label] && value) {
          description += value + units[label] + " ";
        } else if (Array.isArray(value)) {
          description += value.join(', ');
          description += ' ';
        }
        else if (value) {
          description += value;
          description += ' ';
        }
      }

      setPart(prev => ({
        ...prev,
        description: description.trim()[description.length - 1] === ',' ? description.slice(0, -1) : description
      }));
    }
  }, [part.attributes, units]);

  //Handles the changes of the input fields
  const changeHandler = (e) => {
    const { name, value } = e.target;
    const [prefix, field] = name.split('.');
    if (field) {
      setPart(prev => ({
        ...prev,
        [prefix]: {
          ...prev[prefix],
          [field]: value
        }
      }));
    } else {
      setPart(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  // Handles the changes of the units selectors
  const unitsChangeHandler = (event) => {
    setUnits({ ...units, [event.target.name]: event.target.value });
  };

  // Concatenates the units to their corresponding attribute values
  const concatUnits = () => {
    let attributes = { ...part.attributes };
    Object.keys(units).forEach(key => {
      attributes[key] = attributes[key] + ";" + units[key];
    });
    return attributes;
  }

  // Checks if any field is empty and raises its corresponding error flag
  const fieldsValidator = () => {
    let newErrors = {};

    const requiredFields = [
      ['partCategory.parentCategory', 'parentCategory'],
      ['partCategory.categoryCode', 'categoryCode'],
      ['description', 'description'],
      ['manufacturer', 'manufacturer'],
      ['vendor_part_number', 'vendor_part_number']
    ];
    const fieldFullNames = {
      "parentCategory": `Parent Category`,
      "categoryCode": `Part Category`,
      "description": `Description`,
      "manufacturer": `Manufacturer`,
      "vendor_part_number": `Vendor Part Number`
    };

    // Validate required fields
    for (const [path, errorKey] of requiredFields) {
      const value = path.includes('.')
        ? part[path.split('.')[0]][path.split('.')[1]]
        : part[path];
      if (!value?.trim()) {
        newErrors[errorKey] = `The ${fieldFullNames[errorKey]} field is required`;
      }
    }

    // Validate MPN dictionary
    const mpnValues = Object.values(part.mpn || {});
    if (mpnValues.length === 0) {
      newErrors.mpn = "At least one MPN is required";
    } else {
      const invalidMpnKeys = [];
      Object.entries(part.mpn).forEach(([key, mpn]) => {
        if (!mpn?.trim()) {
          invalidMpnKeys.push(key);
        }
      });
      if (invalidMpnKeys.length > 0) {
        newErrors.mpn = `MPN entries at keys ${invalidMpnKeys.join(', ')} cannot be empty`;
      }
    }

    // Validate preferred_mpn
    if (Object.keys(part.mpn || {}).length > 0) {
      if (!part.preferred_mpn || !part.preferred_mpn.toString().trim()) {
        part.preferred_mpn = 0;
      } else if (!Object.keys(part.mpn).includes(part.preferred_mpn.toString())) {
        newErrors.preferred_mpn = "The Preferred MPN must be one of the provided MPN keys";
      }
    }

    // Vendor validation
    const hasVendor1 = part.vendors?.['vendor-1']?.trim();
    const hasVendor2 = part.vendors?.['vendor-2']?.trim();
    const hasLink1 = part.vendors?.['link-1']?.trim();
    const hasLink2 = part.vendors?.['link-2']?.trim();

    if (part.preferred_vendor === "1") {
      if (!hasVendor1) {
        newErrors.vendor1 = "The vendor 1 field is required";
      }
      if (!hasLink1) {
        newErrors.link1 = "The link 1 field is required";
      }
    }

    if (part.preferred_vendor === "2") {
      if (!hasVendor2) {
        newErrors.vendor2 = "The vendor 2 field is required";
      }
      if (!hasLink2) {
        newErrors.link2 = "The link 2 field is required";
      }
    }
    if (image === "") {
      if (!part.partCategory.parentCategory) {
        newErrors.image = "The image field is required";
      } else if (!["SOFTWARE", "DOCUMENTS AND FILES"].includes(part.partCategory.parentCategory)) {
        newErrors.image = "The image field is required";
      }
    }

    // Check if there are any errors
    return newErrors;
  };

  // Checks if the entered part numbers are already used by another part
  const validatePartNumbers = async (fieldErrors) => {
    if (fieldErrors.manufacturer_part_number || fieldErrors.vendor_part_number) return false;

    let partnumErrors = {};
    const usedManuPartNums = usedPartNumbers.manufacturer_part_numbers || [];
    const usedVendorPartNums = usedPartNumbers.vendor_part_numbers || [];

    if (usedManuPartNums.includes(part.manufacturer_part_number)) {
      partnumErrors.manufacturer_part_number = "This Manufacturer Part Number is already used by another part";
    } if (usedVendorPartNums.includes(part.vendor_part_number)) {
      partnumErrors.vendor_part_number = "This Vendor Part Number is already used by another part";
    }
    return partnumErrors;

  }

  const validateAttributes = () => {
    if (!part.partCategory.has_attributes) return {};
    let attributeErrors = {};
    const categoryAttributes = part.partCategory.attributes;

    // Conditional Attribute Rules validation
    if (categoryAttributes[0]?.enableCondAttrs) {
      const attKey = categoryAttributes[0]?.attribute;

      if (part.attributes[attKey]) {
        const type = part.attributes[attKey];
        const rules = categoryAttributes[0][type].split(";");
        for (const key in categoryAttributes) {
          const label = categoryAttributes[key].attribute;
          const unitSpec = categoryAttributes[key]?.units;
          const unitVal = units[label];

          if (rules.includes(key) && !part.attributes[label]) {

            attributeErrors[label] = `The ${label} field is required when ${attKey} is set to ${type}`;

          } else if (rules.includes(key) && unitSpec) {
            if (
              unitVal === undefined ||
              unitVal === null ||
              (typeof unitVal === "string" && unitVal.trim() === "" && unitVal !== " ")
            ) {
              attributeErrors[label] = `The unit for ${label} field is required.`;
            }

          }
        }
      } else {
        attributeErrors[attKey] = `The ${attKey} field is required`;
      }
    } else {
      for (const key in categoryAttributes) {
        const label = categoryAttributes[key].attribute;
        const unitSpec = categoryAttributes[key]?.units;
        const unitVal = units[label];

        if (!part.attributes[label]) {
          attributeErrors[label] = `The ${label} field is required`;
        } else if (unitSpec) {
          if (
            unitVal === undefined ||
            unitVal === null ||
            (typeof unitVal === "string" && unitVal.trim() === "" && unitVal !== " ")
          ) {
            attributeErrors[label] = `The unit for ${label} field is required`;
          }
        }
      }
    }
    return attributeErrors;
  }

  //Submit handler
  const submitHandler = async (e) => {

    e.preventDefault();

    const fieldErrors = fieldsValidator();
    const partnumErrors = await validatePartNumbers(fieldErrors);
    const attributeErrors = part.partCategory.id ? validateAttributes() : {};
    const allErrors = {
      ...fieldErrors,
      ...partnumErrors,
      ...attributeErrors
    };

    setError(allErrors);
    if (Object.values(allErrors).some(err => err) || (allErrors.attributes && Object.values(allErrors.attributes).some(err => err))) {
      console.log("Validation errors:", allErrors);
      return;
    }

    setIsLoading(true);

    const attributes = concatUnits();

    const NewPart = {
      data: {
        type: "part",
        attributes: {
          unique_num: part.unique_num,
          revision_num: part.revision_num,
          description: part.description,
          partCategory: part.partCategory.id,
          attributes: attributes,
          manufacturer: part.manufacturer,
          manufacturer_part_number: part.manufacturer_part_number,
          vendors: part.vendors,
          preferred_vendor: part.preferred_vendor,
          vendor_part_number: part.vendor_part_number,
          status: "Pending Approval",
          folderpath: part.generatedUID,
          mpn: part.mpn,
          preferred_mpn: part.preferred_mpn
        },
        logs: {
          user: await authContext.getCurrentUser()
        }
      }
    };

    try {
      const response = await partCodesService.createPart(NewPart, image, files);

      navigate("/part-code-management", {
        state: { value: false, text: "The Part was succesfully created" },
      });

    } catch (err) {
      console.log(err);
      return null;
    }
  };

  const titleTransition = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
    transition: 'opacity 1s ease-in-out, transform 1s ease-in-out',
  }
  const cardTransition = {
    opacity: isVisible ? 1 : 0,
    transform: isVisible ? 'translateY(0)' : 'translateY(20px)',
    transition: 'opacity 1s ease-in-out, transform 1s ease-in-out',
  }
  const attributeFieldsTransition = {
    transition: 'opacity 0.3s ease-in-out, height 0.3s ease-in-out, margin 0.3s ease-in-out',
    opacity: showAttributes ? 1 : 0,
    height: showAttributes ? 'auto' : '0px',
    marginTop: showAttributes ? '16px' : '0px',
    overflow: 'hidden'
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={5} mb={9}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>

            {/* Title */}
            <MDBox
              mt={6}
              mb={8}
              textAlign="center"
              sx={titleTransition}
            >
              <MDBox mb={1}>
                <MDTypography variant="h3" fontWeight="bold">
                  Create A New Part Code.
                </MDTypography>
              </MDBox>
              <MDBox>
                <MDTypography variant="h5" fontWeight="regular" color="secondary">
                  These informations will be used to describe the new part.
                </MDTypography>
              </MDBox>

            </MDBox>

            {/* Card */}
            <Card sx={cardTransition} >

              {/* Form */}
              <MDBox
                component="form"
                method="POST"
                onSubmit={submitHandler}
                encType="multipart/form-data"
              >
                <MDBox display="flex" flexDirection="column" px={3} my={4}>

                  {/* Loading Spinner or Form Fields */}
                  {isLoading ? (
                    <MDBox display="flex" justifyContent="center" alignItems="center" height="84vh">
                      <CircularProgress />
                    </MDBox>
                  ) : (
                    <MDBox>
                      {/* General Informations Fields */}

                      <MDTypography variant="h5" fontWeight="bold" mt={1}>
                        General Informations :
                      </MDTypography>
                      <GeneralInfoFields changeHandler={changeHandler} part={part} setPart={part} error={error} />

                      {/*Image and Attachments Fields */}
                      <MDTypography variant="h5" fontWeight="bold" mt={1}>
                        Image & Attachments :
                      </MDTypography>

                      <Grid container spacing={3}>
                        <Grid item xs={12} sm={6}>
                          <ImageField image={image} setImage={setImage} error={error} /> {/* Image Field */}
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <AttachmentsField files={files} setFiles={setFiles} /> {/* Attachments Field */}
                        </Grid>
                      </Grid>

                      {/* Attributes Fields */}
                      <MDBox sx={attributeFieldsTransition}>
                        {showAttributes && (
                          <>
                            <AttributesFields
                              part={part}
                              setPart={setPart}
                              changeHandler={changeHandler}
                              error={error}
                              units={units}
                              unitsChangeHandler={unitsChangeHandler}
                            />
                          </>
                        )}
                      </MDBox>
                    </MDBox>
                  )}

                  {/* Submit & Cancel buttons */}
                  <MDBox ml="auto" mt={4} mb={2} display="flex" justifyContent="flex-end">

                    <MDBox mx={1}>
                      <MDButton
                        variant="gradient"
                        color="dark"
                        size="small"
                        px={2}
                        mx={2}
                        onClick={() =>
                          navigate("/part-code-management/part-code-view", {
                            state: { value: false, text: "" },
                          })
                        }
                      >
                        Cancel
                      </MDButton>
                    </MDBox>
                    <MDBox mx={1}>
                      <MDButton variant="gradient" color="dark" size="small" type="submit">
                        Create
                      </MDButton>
                    </MDBox>
                  </MDBox>

                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  )
};
export default NewPart;